/**
 * Schema Parser — Parses raw DDL text into a structured schema model.
 *
 * Handles IDMS-generated DDL with:
 *   - CREATE TABLE with duplicate columns, PIC comments, bad DECIMAL precision
 *   - CREATE OR REPLACE PROCEDURE (DB2 format)
 *   - CREATE INDEX / CREATE VIEW
 *
 * Input: raw SQL text (from individual .sql files or combined all_ddl.sql)
 * Output: { tables: [], procedures: [], views: [], indexes: [] }
 */

// ---------------------------------------------------------------------------
// Parse a PIC comment to extract the actual COBOL PIC clause
// e.g. "-- PIC X(4)" → { type: 'X', length: 4 }
//      "-- PIC XX"    → { type: 'X', length: 2 }
//      "-- PIC XXXX"  → { type: 'X', length: 4 }
//      "-- PIC 9(9)"  → { type: '9', length: 9 }
//      "-- PIC S9(7)V9999" → { type: 'S9V9', intDigits: 7, decDigits: 4 }
// ---------------------------------------------------------------------------
export function parsePicComment(comment) {
  if (!comment) return null
  // Strip leading "-- PIC " or "-- PIC"
  const picMatch = comment.match(/--\s*PIC\s+(.+)/i)
  if (!picMatch) return null
  const pic = picMatch[1].trim().replace(/,\s*$/, '') // strip trailing comma

  // Signed numeric with decimal: S9(n)V9(m) or S9(n)V9999
  const signedDecMatch = pic.match(/^S?9\((\d+)\)V9\((\d+)\)$/i)
  if (signedDecMatch) {
    return { type: 'decimal', intDigits: parseInt(signedDecMatch[1]), decDigits: parseInt(signedDecMatch[2]) }
  }
  const signedDecMatch2 = pic.match(/^S?9\((\d+)\)V(9+)$/i)
  if (signedDecMatch2) {
    return { type: 'decimal', intDigits: parseInt(signedDecMatch2[1]), decDigits: signedDecMatch2[2].length }
  }

  // Unsigned numeric with decimal: 9(n)V99
  const unsignedDecMatch = pic.match(/^9\((\d+)\)V(9+)$/i)
  if (unsignedDecMatch) {
    return { type: 'decimal', intDigits: parseInt(unsignedDecMatch[1]), decDigits: unsignedDecMatch[2].length }
  }

  // Signed numeric with decimal using V9(m): S9V99
  const shortSignedDec = pic.match(/^S?9V(9+)$/i)
  if (shortSignedDec) {
    return { type: 'decimal', intDigits: 1, decDigits: shortSignedDec[1].length }
  }

  // Signed numeric with decimal: S999V9999
  const allDigitDec = pic.match(/^S?(9+)V(9+)$/i)
  if (allDigitDec) {
    return { type: 'decimal', intDigits: allDigitDec[1].length, decDigits: allDigitDec[2].length }
  }

  // Pure numeric: S9(n) or 9(n)
  const numMatch = pic.match(/^S?9\((\d+)\)$/i)
  if (numMatch) {
    return { type: 'integer', length: parseInt(numMatch[1]) }
  }

  // Short numeric: S999 or 999
  const shortNumMatch = pic.match(/^S?(9+)$/i)
  if (shortNumMatch) {
    return { type: 'integer', length: shortNumMatch[1].length }
  }

  // Pure numeric: 9
  if (/^9$/.test(pic)) {
    return { type: 'integer', length: 1 }
  }

  // Alpha with parens: X(n)
  const alphaMatch = pic.match(/^X\((\d+)\)$/i)
  if (alphaMatch) {
    return { type: 'alpha', length: parseInt(alphaMatch[1]) }
  }

  // Alpha shorthand: XX, XXX, XXXX
  const alphaShort = pic.match(/^(X+)$/i)
  if (alphaShort) {
    return { type: 'alpha', length: alphaShort[1].length }
  }

  // Alphanumeric with parens: X(0003) → 3
  const alphaZeroPad = pic.match(/^X\(0*(\d+)\)$/i)
  if (alphaZeroPad) {
    return { type: 'alpha', length: parseInt(alphaZeroPad[1]) }
  }

  return null
}

// ---------------------------------------------------------------------------
// Parse a single column definition line
// e.g. '    "NBR_PA_KEY_1572" INTEGER NOT NULL  -- PIC 9(9),'
// ---------------------------------------------------------------------------
function parseColumnLine(line) {
  const trimmed = line.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('--')) return null  // pure comment line
  if (/^\s*CONSTRAINT\s/i.test(trimmed)) return null  // constraint line

  // Extract inline comment
  const commentIdx = trimmed.indexOf('  --')
  let sqlPart = commentIdx >= 0 ? trimmed.substring(0, commentIdx).trim() : trimmed.trim()
  const comment = commentIdx >= 0 ? trimmed.substring(commentIdx).trim() : null

  // Remove trailing comma from SQL part
  sqlPart = sqlPart.replace(/,\s*$/, '')

  // Parse column name (quoted or unquoted)
  const colMatch = sqlPart.match(/^"([^"]+)"\s+(.+)$/i) || sqlPart.match(/^(\w+)\s+(.+)$/i)
  if (!colMatch) return null

  const name = colMatch[1]
  const rest = colMatch[2]

  // Parse type and constraints
  const nullable = !/\bNOT\s+NULL\b/i.test(rest)
  const typeMatch = rest.match(/^(\w+(?:\([^)]*\))?)/i)
  const type = typeMatch ? typeMatch[1] : rest.split(/\s/)[0]

  // Parse PIC comment
  const pic = parsePicComment(comment)

  return { name, type, nullable, picComment: comment, pic, rawLine: line }
}

// ---------------------------------------------------------------------------
// Parse a PRIMARY KEY constraint
// e.g. 'CONSTRAINT "PK_ACT_EXCPT_OFFSET" PRIMARY KEY ("COL1", "COL2")'
// ---------------------------------------------------------------------------
function parsePrimaryKey(line) {
  const pkMatch = line.match(/CONSTRAINT\s+"?(\w+)"?\s+PRIMARY\s+KEY\s*\(([^)]+)\)/i)
  if (!pkMatch) return null

  const name = pkMatch[1]
  const columns = pkMatch[2]
    .split(',')
    .map(c => c.trim().replace(/"/g, ''))

  return { name, columns }
}

// ---------------------------------------------------------------------------
// Parse all CREATE TABLE blocks from SQL text
// ---------------------------------------------------------------------------
function parseTables(sqlText) {
  const tables = []
  const regex = /CREATE\s+TABLE\s+"?([^"\s(]+)"?\s*\(([\s\S]*?)\)\s*;/gi
  let match

  while ((match = regex.exec(sqlText)) !== null) {
    const tableName = match[1].replace(/"/g, '')
    const body = match[2]
    const rawDdl = match[0]

    const lines = body.split('\n')
    const columns = []
    let primaryKey = null

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed) continue

      // Check for CONSTRAINT PRIMARY KEY
      const pk = parsePrimaryKey(trimmed)
      if (pk) {
        primaryKey = pk
        continue
      }

      // Try to parse as column
      const col = parseColumnLine(line)
      if (col) {
        columns.push(col)
      }
    }

    tables.push({
      name: tableName,
      columns,
      primaryKey,
      rawDdl,
    })
  }

  return tables
}

// ---------------------------------------------------------------------------
// Parse all stored procedures from SQL text
// Handles: CREATE OR REPLACE PROCEDURE ... BEGIN ... END;
// ---------------------------------------------------------------------------
function parseProcedures(sqlText) {
  const procedures = []
  const regex = /(?:--[^\n]*\n)*CREATE\s+(?:OR\s+(?:ALTER|REPLACE)\s+)?PROCEDURE\s+"?([^"\s(]+)"?\s*\([^)]*\)[\s\S]*?BEGIN[\s\S]*?END\s*;/gi
  let match

  while ((match = regex.exec(sqlText)) !== null) {
    const procName = match[1].replace(/"/g, '')
    const rawDdl = match[0]

    // Try to extract table name and operation from proc name
    // Convention: sp_TABLENAME_Operation (e.g. sp_ACT_EXCPT_OFFSET_Select)
    let tableName = null
    let operation = null
    const nameMatch = procName.match(/^sp_(.+)_(Select|Insert|Update|Delete)$/i)
    if (nameMatch) {
      tableName = nameMatch[1]
      operation = nameMatch[2].toLowerCase()
    }

    procedures.push({
      name: procName,
      tableName,
      operation,
      rawDdl,
    })
  }

  return procedures
}

// ---------------------------------------------------------------------------
// Parse CREATE INDEX statements
// ---------------------------------------------------------------------------
function parseIndexes(sqlText) {
  const indexes = []
  const regex = /CREATE\s+(?:UNIQUE\s+)?INDEX\s+"?([^"\s(]+)"?[\s\S]*?;/gi
  let match

  while ((match = regex.exec(sqlText)) !== null) {
    indexes.push({
      name: match[1].replace(/"/g, ''),
      rawDdl: match[0],
    })
  }

  return indexes
}

// ---------------------------------------------------------------------------
// Parse CREATE VIEW statements
// ---------------------------------------------------------------------------
function parseViews(sqlText) {
  const views = []
  const regex = /CREATE\s+(?:OR\s+REPLACE\s+)?VIEW\s+"?([^"\s(]+)"?[\s\S]*?;/gi
  let match

  while ((match = regex.exec(sqlText)) !== null) {
    views.push({
      name: match[1].replace(/"/g, ''),
      rawDdl: match[0],
    })
  }

  return views
}

// ---------------------------------------------------------------------------
// Detect SQL dialect from DDL text
// ---------------------------------------------------------------------------
export function detectDialect(sqlText) {
  // Check for DB2 patterns
  if (/CREATE\s+OR\s+REPLACE\s+PROCEDURE/i.test(sqlText)) return 'db2'
  if (/LANGUAGE\s+SQL/i.test(sqlText)) return 'db2'
  if (/DECLARE\s+EXIT\s+HANDLER/i.test(sqlText)) return 'db2'
  if (/SIGNAL\s+SQLSTATE/i.test(sqlText)) return 'db2'

  // Check for SQL Server patterns
  if (/CREATE\s+OR\s+ALTER\s+PROCEDURE/i.test(sqlText)) return 'sqlserver'
  if (/\[[\w]+\]/g.test(sqlText)) return 'sqlserver' // bracket identifiers
  if (/NVARCHAR/i.test(sqlText)) return 'sqlserver'
  if (/BEGIN\s+TRY/i.test(sqlText)) return 'sqlserver'

  // Check for PostgreSQL patterns
  if (/CREATE\s+OR\s+REPLACE\s+FUNCTION/i.test(sqlText)) return 'postgresql'
  if (/\$\$.*\$\$/s.test(sqlText)) return 'postgresql'

  // Check for MySQL patterns
  if (/`[\w]+`/g.test(sqlText)) return 'mysql' // backtick identifiers
  if (/ENGINE\s*=/i.test(sqlText)) return 'mysql'

  // Default: check identifier style
  if (/"[\w]+"/.test(sqlText)) return 'db2' // double-quoted = ANSI/DB2 default

  return 'unknown'
}

// ---------------------------------------------------------------------------
// Main parse function — takes raw SQL text, returns structured schema
// ---------------------------------------------------------------------------
export function parseSchema(sqlText) {
  const tables = parseTables(sqlText)
  const procedures = parseProcedures(sqlText)
  const indexes = parseIndexes(sqlText)
  const views = parseViews(sqlText)
  const dialect = detectDialect(sqlText)

  return {
    tables,
    procedures,
    indexes,
    views,
    dialect,
    stats: {
      tableCount: tables.length,
      procedureCount: procedures.length,
      indexCount: indexes.length,
      viewCount: views.length,
      totalColumns: tables.reduce((sum, t) => sum + t.columns.length, 0),
    },
  }
}

// ---------------------------------------------------------------------------
// Parse files from a zip structure
// Takes an object: { files: [{ name, path, content }] }
// where files come from reading the zip on the client side
// ---------------------------------------------------------------------------
export function parseSchemaFiles(files) {
  // Separate DDL files from procedure files
  const ddlFiles = []
  const procFiles = []

  for (const file of files) {
    if (!file.name.endsWith('.sql')) continue
    const pathLower = file.path?.toLowerCase() || file.name.toLowerCase()

    // Skip combined files — we'll parse individual files for better structure
    if (file.name === 'all_ddl.sql' || file.name === 'all_procedures.sql') continue

    if (pathLower.includes('/procedures/') || pathLower.includes('\\procedures\\')) {
      procFiles.push(file)
    } else if (pathLower.includes('/ddl/') || pathLower.includes('\\ddl\\')) {
      ddlFiles.push(file)
    } else {
      // Try to auto-detect from content
      if (/CREATE\s+TABLE/i.test(file.content)) {
        ddlFiles.push(file)
      } else if (/CREATE\s+(?:OR\s+(?:ALTER|REPLACE)\s+)?PROCEDURE/i.test(file.content)) {
        procFiles.push(file)
      }
    }
  }

  // If no individual files found, fall back to combined files
  if (ddlFiles.length === 0 && procFiles.length === 0) {
    const allDdl = files.find(f => f.name === 'all_ddl.sql')
    const allProcs = files.find(f => f.name === 'all_procedures.sql')
    if (allDdl) ddlFiles.push(allDdl)
    if (allProcs) procFiles.push(allProcs)
  }

  // Parse all DDL files
  const allDdlText = ddlFiles.map(f => f.content).join('\n\n')
  const allProcText = procFiles.map(f => f.content).join('\n\n')
  const combinedText = allDdlText + '\n\n' + allProcText

  const result = parseSchema(combinedText)

  // Enrich procedures with table name from file path if not parsed from name
  for (const proc of result.procedures) {
    if (!proc.tableName) {
      // Try to find from file path: procedures/TABLE_NAME/sp_name.sql
      const matchingFile = procFiles.find(f => f.content.includes(proc.name))
      if (matchingFile) {
        const pathParts = (matchingFile.path || matchingFile.name).split(/[/\\]/)
        const procIdx = pathParts.findIndex(p => p.toLowerCase() === 'procedures')
        if (procIdx >= 0 && procIdx + 1 < pathParts.length) {
          proc.tableName = pathParts[procIdx + 1]
        }
      }
    }
  }

  return {
    ...result,
    fileStats: {
      ddlFiles: ddlFiles.length,
      procFiles: procFiles.length,
    },
  }
}
